import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { userApi } from '../api/user';
import { serviceApi } from '../api/service';
import { UserResponse, UserStatus, GlobalRole, ApprovalStatus } from '../types';
import { UserPlus, RefreshCcw, X, Calendar, Hash, MoreHorizontal, Ticket, Search, ArrowUpDown } from 'lucide-react';
import { getServiceDisplayName, getServiceRoleLabel } from '../utils/roles';
import LoadingScreen from '../components/LoadingScreen';

type UserServicePermission = {
  code: string;
  name: string;
  role: string | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getPermissionTitle = (permission: UserServicePermission) => {
  const roleLabel = permission.role ? getServiceRoleLabel(permission.code, permission.role as any) : '서비스 멤버';
  return `${getServiceDisplayName(permission.code)} · ${roleLabel}`;
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissionMap, setPermissionMap] = useState<Record<number, UserServicePermission[]>>({});
  const [permissionMapLoading, setPermissionMapLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'createdAt'>('name');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', globalRole: 'NONE' as GlobalRole });
  const [createLoading, setCreateLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionSummary, setPermissionSummary] = useState<UserServicePermission[]>([]);
  const [adTicketLoading, setAdTicketLoading] = useState(false);

  const loadUsersPermissionSummary = useCallback(async (targetUsers: UserResponse[]) => {
    setPermissionMapLoading(true);
    try {
      const servicesRes = await serviceApi.getServices();
      const allServices = servicesRes.data.result || [];
      const nextPermissionMap: Record<number, UserServicePermission[]> = {};

      await Promise.all(
        targetUsers.map(async (user) => {
          const servicePermissions: UserServicePermission[] = [];

          for (const service of allServices) {
            try {
              const serviceMembersRes = await serviceApi.getMembers(service.code);
              const serviceMember = serviceMembersRes.data.result?.find(
                (member) => member.userId === user.id && member.status === 'ACTIVE'
              ) || null;

              if (serviceMember) {
                servicePermissions.push({
                  code: service.code,
                  name: service.name,
                  role: serviceMember.role,
                });
              }
            } catch {
              continue;
            }
          }

          nextPermissionMap[user.id] = servicePermissions;
        })
      );

      setPermissionMap(nextPermissionMap);
    } catch {
      setPermissionMap({});
    } finally {
      setPermissionMapLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userApi.getUsers();
      const nextUsers = response.data.result || [];
      setUsers(nextUsers);
      loadUsersPermissionSummary(nextUsers);
    } catch {
      setError('유저 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [loadUsersPermissionSummary]);

  const visibleUsers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = users.filter((userItem) => {
      if (!normalized) return true;
      return (
        userItem.name.toLowerCase().includes(normalized) ||
        userItem.email.toLowerCase().includes(normalized)
      );
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'createdAt') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }

      const left = sortBy === 'email' ? a.email : a.name;
      const right = sortBy === 'email' ? b.email : b.name;
      return left.localeCompare(right, 'ko');
    });
  }, [query, sortBy, users]);

  const loadUserPermissions = async (userId: number) => {
    setPermissionLoading(true);
    try {
      const servicesRes = await serviceApi.getServices();
      const allServices = servicesRes.data.result || [];
      const servicePermissions: UserServicePermission[] = [];

      for (const service of allServices) {
        try {
          const serviceMembersRes = await serviceApi.getMembers(service.code);
          const serviceMember = serviceMembersRes.data.result?.find(
            (member) => member.userId === userId && member.status === 'ACTIVE'
          ) || null;

          if (serviceMember) {
            servicePermissions.push({
              code: service.code,
              name: service.name,
              role: serviceMember.role,
            });
          }
        } catch {
          continue;
        }
      }

      setPermissionSummary(servicePermissions);
    } catch {
      setPermissionSummary([]);
    } finally {
      setPermissionLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!showDetailModal || !selectedUser) return;
    loadUserPermissions(selectedUser.id);
  }, [showDetailModal, selectedUser]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await userApi.createUser(formData);
      setShowCreateModal(false);
      setFormData({ email: '', password: '', name: '', globalRole: 'NONE' });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || '유저 생성 실패');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleStatusUpdate = async (userId: number, status: UserStatus) => {
    try {
      await userApi.updateUser(userId, { status });
      fetchUsers();
      if (selectedUser?.id === userId) {
        const res = await userApi.getUser(userId);
        if (res.data.result) setSelectedUser(res.data.result);
      }
    } catch {
      alert('상태 변경 실패');
    }
  };

  const handleApprovalUpdate = async (userId: number, approvalStatus: ApprovalStatus) => {
    try {
      await userApi.updateApproval(userId, { approvalStatus });
      fetchUsers();
      if (selectedUser?.id === userId) {
        const res = await userApi.getUser(userId);
        if (res.data.result) setSelectedUser(res.data.result);
      }
    } catch {
      alert('승인 상태 변경 실패');
    }
  };

  const handleAdTicketUpdate = async (userId: number, hasAdRemovalTicket: boolean) => {
    setAdTicketLoading(true);
    try {
      await userApi.updateAdRemovalTicket(userId, { purchased: hasAdRemovalTicket });
      fetchUsers();
      if (selectedUser?.id === userId) {
        const res = await userApi.getUser(userId);
        if (res.data.result) setSelectedUser(res.data.result);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || '광고 제거 티켓 상태 변경 실패');
    } finally {
      setAdTicketLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header page-header-row">
        <div>
          <h1 className="page-title page-title-sm">유저 관리</h1>
          <p className="page-subtitle">시스템에 등록된 전체 사용자 계정을 관리합니다.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-outline" onClick={fetchUsers} disabled={loading}>
            <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <UserPlus size={18} />
            <span>유저 추가</span>
          </button>
        </div>
      </div>

      <div className="dashboard-admin-toolbar" style={{ justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div className="dashboard-admin-search">
          <Search size={16} />
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="이름 또는 이메일 검색"
          />
        </div>
        <label className="dashboard-admin-sort">
          <ArrowUpDown size={16} />
          <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
            <option value="name">이름순</option>
            <option value="email">이메일순</option>
            <option value="createdAt">가입일 최신순</option>
          </select>
        </label>
      </div>

      <div className="table-container shadow">
        {error && (
          <div style={{ padding: '0.75rem 1rem', color: '#b91c1c', background: '#fee2e2', borderBottom: '1px solid #fecaca' }}>
            {error}
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th>사용자</th>
              <th>보유 서비스 권한</th>
              <th>상태</th>
              <th>승인</th>
              <th>광고 제거</th>
              <th>가입일</th>
              <th style={{ textAlign: 'right' }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '3rem 1rem' }}>
                  <LoadingScreen inline title="유저 목록을 불러오는 중" copy="잠시만 기다려 주세요." />
                </td>
              </tr>
            ) : visibleUsers.map((userItem) => (
              <tr key={userItem.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#eff6ff', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>
                      {userItem.name.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600' }}>{userItem.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{userItem.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {permissionMapLoading ? (
                    <LoadingScreen inline title="권한 조회 중" copy="서비스 권한을 불러오고 있습니다." />
                  ) : permissionMap[userItem.id]?.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {permissionMap[userItem.id].slice(0, 3).map((servicePermission) => (
                        <span
                          key={servicePermission.code}
                          className="badge badge-active user-permission-chip"
                          style={{ textTransform: 'none' }}
                          title={getPermissionTitle(servicePermission)}
                        >
                          {getServiceDisplayName(servicePermission.code)}
                        </span>
                      ))}
                      {permissionMap[userItem.id].length > 3 && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          외 {permissionMap[userItem.id].length - 3}개 서비스
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>없음</span>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${userItem.status === 'INACTIVE' ? 'badge-status-inactive' : `badge-${userItem.status.toLowerCase()}`}`}
                  >
                    {userItem.status}
                  </span>
                </td>
                <td>
                  <span className={`badge ${userItem.approvalStatus === 'APPROVED' ? 'badge-active' : 'badge-inactive'}`}>
                    {userItem.approvalStatus}
                  </span>
                </td>
                <td>
                  <span className={`badge ${userItem.hasAdRemovalTicket ? 'badge-active' : 'badge-inactive'}`}>
                    {userItem.hasAdRemovalTicket ? '보유' : '무료'}
                  </span>
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                  {formatDate(userItem.createdAt)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-outline" style={{ padding: '0.4rem' }} onClick={() => { setSelectedUser(userItem); setShowDetailModal(true); }}>
                    <MoreHorizontal size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ padding: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800' }}>새 유저 생성</h2>
              <button onClick={() => setShowCreateModal(false)} className="btn btn-outline" style={{ padding: '0.4rem', border: 'none' }}>
                <X />
              </button>
            </div>
            <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>이름</label>
                <input className="input" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="홍길동" />
              </div>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>이메일</label>
                <input type="email" className="input" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="example@omong.kr" />
              </div>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>비밀번호</label>
                <input type="password" className="input" required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" />
              </div>
              <div>
                <label className="label" style={{ marginBottom: '0.5rem' }}>권한</label>
                <select className="input" value={formData.globalRole} onChange={(e) => setFormData({ ...formData, globalRole: e.target.value as GlobalRole })}>
                  <option value="NONE">일반 사용자</option>
                </select>
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>취소</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={createLoading}>
                  {createLoading ? '생성 중...' : '유저 생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ padding: '2.5rem', maxWidth: '560px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800' }}>유저 상세 설정</h2>
              <button onClick={() => setShowDetailModal(false)} className="btn btn-outline" style={{ padding: '0.4rem', border: 'none' }}>
                <X />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="card" style={{ background: '#f8fafc', border: 'none', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: '800', color: 'var(--primary-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  {selectedUser.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: '800', fontSize: '1.125rem' }}>{selectedUser.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{selectedUser.email}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    <Hash size={14} /> ID
                  </div>
                  <div style={{ fontWeight: '700' }}>{selectedUser.id}</div>
                </div>
                <div className="card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                    <Calendar size={14} /> 가입일
                  </div>
                  <div style={{ fontWeight: '700' }}>{formatDateTime(selectedUser.createdAt)}</div>
                </div>
              </div>

              <div>
                <label className="label" style={{ marginBottom: '1rem' }}>계정 상태 변경</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {(['ACTIVE', 'INACTIVE', 'LOCKED'] as UserStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusUpdate(selectedUser.id, status)}
                      className={`btn ${selectedUser.status === status ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1, fontSize: '0.75rem' }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label" style={{ marginBottom: '1rem' }}>보유 서비스 권한</label>
                {selectedUser.globalRole === 'ROOT_ADMIN' && (
                  <div style={{ marginBottom: '0.75rem', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.875rem', fontWeight: '700' }}>
                    시스템 최고관리자
                  </div>
                )}
                {permissionLoading ? (
                  <LoadingScreen inline title="권한 정보를 불러오는 중" copy="서비스 권한을 조회하고 있습니다." />
                ) : permissionSummary.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {permissionSummary.map((servicePermission) => (
                      <span
                        key={servicePermission.code}
                        className="badge badge-active user-permission-chip"
                        style={{ textTransform: 'none', width: 'fit-content' }}
                        title={getPermissionTitle(servicePermission)}
                      >
                        {getServiceDisplayName(servicePermission.code)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--text-muted)' }}>연결된 서비스 권한이 없습니다.</div>
                )}
              </div>

              <div>
                <label className="label" style={{ marginBottom: '1rem' }}>광고 제거 티켓</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`badge ${selectedUser.hasAdRemovalTicket ? 'badge-active' : 'badge-inactive'}`}>
                    {selectedUser.hasAdRemovalTicket ? '보유' : '미보유'}
                  </span>
                  <button
                    type="button"
                    className={`btn ${selectedUser.hasAdRemovalTicket ? 'btn-outline' : 'btn-primary'}`}
                    onClick={() => handleAdTicketUpdate(selectedUser.id, !selectedUser.hasAdRemovalTicket)}
                    disabled={adTicketLoading}
                  >
                    <Ticket size={16} />
                    <span>{selectedUser.hasAdRemovalTicket ? '무료로 변경' : '티켓 부여'}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="label" style={{ marginBottom: '1rem' }}>승인 상태 관리</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {(['PENDING', 'APPROVED', 'REJECTED'] as ApprovalStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleApprovalUpdate(selectedUser.id, status)}
                      className={`btn ${selectedUser.approvalStatus === status ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1, fontSize: '0.75rem' }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)', textAlign: 'right' }}>
              <button className="btn btn-outline" onClick={() => setShowDetailModal(false)}>
                창 닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
